import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { Outlet, useLocation } from '@tanstack/react-router'
import { Component, Suspense, useMemo } from 'react'

import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import Stack from '@mui/material/Stack'
import { ThemeProvider, createTheme } from '@mui/material/styles'

import { userSettingsGet } from 'Common/api/SettingsApi'
import { Authentication } from 'Common/auth/types'
import RightsDialog from 'Common/components/RightsDialog'
import MainNavigationContainer from 'Common/components/navigation/MainNavigationContainer'
import { bottomNavHeight } from 'Common/components/navigation/MainNavigationNew'
import { mobileSubNavClearance } from 'Common/components/navigation/NewSubNavigationMenu'
import PrimaryAppBar from 'Common/components/navigation/PrimaryAppBar'
import { PageSkeleton, pageSkeletonVariant } from 'Common/components/skeletons'
import { hasFeature } from 'Common/environment'
import { getEnvironment } from 'Common/environment/environment'
import queryKeys from 'Common/queryKeys/queryKeys'
import { useMobileTopNavStore } from 'Common/store/MobileTopNavStore'
import { useUserSettingsStore } from 'Common/store/useUserSettingsStore'
import 'Common/styles/opusGlobalMain.css'
import { HideAgeAndSubGroupFilters } from 'Common/utils/featureConfig'
import { RequestStaleTime } from 'Common/utils/helpers/HelperQuery'
import { useAuth } from 'Common/utils/query/useAuth'
import { usePrefetchSharedMetadata } from 'Common/utils/query/useMetadata'

import mockingbirdNewNavTheme from '../styles/themes/mockingbirdNewNavTheme'

const newNavTheme = createTheme(mockingbirdNewNavTheme)

type BaseAppProps = {
    logout(..._args: unknown[]): unknown
    children: React.ReactNode
}

type BaseAppInnerProps = BaseAppProps & {
    auth: Authentication
    hasMobileSubNav: boolean
    routeKey: string
    skeletonVariant: ReturnType<typeof pageSkeletonVariant>
}

function GetEnvironmentAndSettings({ auth }: { auth: Authentication }) {
    const setUserSettings = useUserSettingsStore((state) => state.setUserSettings)
    const publishingHouseGroupIds = (auth?.data?.opus_user?.publishingHouseGroups ?? []).map(
        ({ id }) => id,
    )
    const hideAgeAndSubGroup = HideAgeAndSubGroupFilters(publishingHouseGroupIds)

    usePrefetchSharedMetadata()

    useSuspenseQuery({
        queryKey: [queryKeys.userSettings],
        queryFn: async () => {
            const userSettings = await userSettingsGet()
            setUserSettings(userSettings, { hideAgeAndSubGroup })
            return userSettings
        },
    })
    useQuery({
        queryKey: [queryKeys.environment],
        queryFn: getEnvironment,
        staleTime: RequestStaleTime.configuration,
    })
    return <></>
}

export const baseAppInit = () => {
    class BaseApp extends Component<BaseAppInnerProps> {
        componentDidMount() {
            const rootLoader = document.getElementById('rootLoader')
            if (rootLoader) {
                rootLoader.style.opacity = '0'
            }
        }

        renderRightsDialog() {
            if (this.props.auth?.data?.opus_user_id) return null
            return <RightsDialog />
        }

        render() {
            const { auth, logout, hasMobileSubNav, routeKey, skeletonVariant } = this.props
            const isNewNav = hasFeature('newNavigation')

            const shell = (
                <Box sx={{ display: 'flex' }}>
                    <CssBaseline />
                    {!isNewNav && <PrimaryAppBar logout={logout} />}
                    <GetEnvironmentAndSettings auth={auth} />
                    <Stack direction={'row'} spacing={0} sx={{ width: '100%' }}>
                        <MainNavigationContainer auth={auth} logout={logout} />
                        <Box
                            component="main"
                            sx={{
                                flexGrow: 1,
                                minWidth: 0,
                                pb: isNewNav
                                    ? {
                                          xs: `calc(${bottomNavHeight}px + env(safe-area-inset-bottom))`,
                                          md: 0,
                                      }
                                    : 0,
                                pt:
                                    isNewNav && hasMobileSubNav
                                        ? { xs: `${mobileSubNavClearance}px`, md: 0 }
                                        : 0,
                            }}
                        >
                            {!isNewNav && (
                                <Box
                                    sx={{
                                        height: (theme) => theme.mixins.toolbar.minHeight,
                                        flexShrink: 0,
                                        displayPrint: 'none',
                                    }}
                                />
                            )}
                            <Suspense
                                fallback={<PageSkeleton variant={skeletonVariant} />}
                                key={routeKey}
                            >
                                <Outlet />
                            </Suspense>
                        </Box>
                    </Stack>
                </Box>
            )

            return isNewNav ? (
                <ThemeProvider theme={newNavTheme} defaultMode="light">
                    {shell}
                </ThemeProvider>
            ) : (
                shell
            )
        }
    }
    return function BaseAppWithAuth(props: BaseAppProps) {
        const auth = useAuth()
        const hasMobileSubNav = useMobileTopNavStore((state) => state.hasMobileTopNav)
        const location = useLocation()
        const routeKey = useMemo(
            () => location.pathname.split('/').filter(Boolean).slice(0, 2).join('/'),
            [location.pathname],
        )
        return (
            <BaseApp
                {...props}
                auth={auth}
                hasMobileSubNav={hasMobileSubNav}
                routeKey={routeKey}
                skeletonVariant={pageSkeletonVariant(location.pathname)}
            />
        )
    }
}
